/**
 * Auth Context
 * 
 * Manages authentication state and provides auth methods to the entire app.
 * Wraps Supabase auth for user session management.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, Session, AuthError, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';

// Extended user type with subscription info from profiles table
export interface AuthUser extends User {
  subscription_status?: 'free' | 'premium' | 'expired';
  subscription_expires_at?: string | null;
}

export interface AuthContextType {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  isPasswordRecovery: boolean;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  // Fetch user profile data (subscription status) from profiles table
  const fetchUserProfile = useCallback(async (userId: string): Promise<Partial<AuthUser>> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('subscription_status, subscription_expires_at')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        return {};
      }

      return {
        subscription_status: data?.subscription_status || 'free',
        subscription_expires_at: data?.subscription_expires_at,
      };
    } catch (err) {
      console.error('Error fetching profile:', err);
      return {};
    }
  }, []);

  // Update user state with profile data
  const updateUserWithProfile = useCallback(async (authUser: User | null) => {
    if (!authUser) {
      setUser(null);
      return;
    }

    const profile = await fetchUserProfile(authUser.id);
    setUser({
      ...authUser,
      ...profile,
    });
  }, [fetchUserProfile]);

  useEffect(() => {
    // Skip auth initialization if authentication is disabled
    if (!supabase) {
      setLoading(false);
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }: { data: { session: Session | null } }) => {
      setSession(session);
      if (session?.user) {
        updateUserWithProfile(session.user);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
      
      setSession(session);
      if (session?.user) {
        updateUserWithProfile(session.user);
      } else {
        setUser(null);
      }
      
      // Handle password recovery event
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      } else if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        setIsPasswordRecovery(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [updateUserWithProfile]);

  // Sign up with email/password
  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) return { error };

      // Profile is automatically created by database trigger
      if (data.user) {
        await updateUserWithProfile(data.user);
      }

      return { error: null };
    } catch (err) {
      console.error('Sign up error:', err);
      return { error: err as AuthError };
    }
  };

  // Sign in with email/password
  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) return { error };

      if (data.user) {
        await updateUserWithProfile(data.user);
      }

      return { error: null };
    } catch (err) {
      console.error('Sign in error:', err);
      return { error: err as AuthError };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) return { error };

      setUser(null);
      setSession(null);

      return { error: null };
    } catch (err) {
      console.error('Sign out error:', err);
      return { error: err as AuthError };
    }
  };

  // Reset password - sends password reset email
  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}`,
      });

      if (error) return { error };

      return { error: null };
    } catch (err) {
      console.error('Password reset error:', err);
      return { error: err as AuthError };
    }
  };

  // Update password (for users in recovery session)
  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ 
        password: newPassword 
      });

      if (error) return { error };

      return { error: null };
    } catch (err) {
      console.error('Password update error:', err);
      return { error: err as AuthError };
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    isPasswordRecovery,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
